import React from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Image, Text } from 'rebass/styled-components';
import { ArrowRight, Check, Rocket, TrendingUp } from 'lucide-react';

import { Button, Checkbox } from '@getro/rombo';
import { RocketLaunch } from './rocketLaunch';
import { APPLICATION_BOOST_CREDITS, CALIBRATION_AGENT_CREDITS } from '../hooks/useAgentBoost';
import IconStart from '../../../../assets/icon-stars.svg';
import { CONTACT_AGENT_BOOST_MEETING_URL } from '../../../../helpers/constants';

export const AgentBoost = ({
  handleStartAgent,
  handleCalibrationToggle,
  companyName,
  jobTitle,
  isCalibrationAgentSelected,
  totalCredits,
  isLoading,
  availableAgentCredits,
}) => (
  <Box sx={{ gap: '32px', display: 'flex', flexDirection: 'column', lineHeight: '1.4' }}>
    <Flex alignItems="flex-start" mb="8px" position="relative" sx={{ gap: '8px' }}>
      <Box
        width="72px"
        height="72px"
        flexShrink={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <RocketLaunch width={72} height={72} />
      </Box>
      <Box flex="1">
        <Text fontSize="20px" fontWeight="semibold" color="text.dark" mb="8px">
          Boost{' '}
          <Text as="span" fontStyle="italic">
            {jobTitle}
          </Text>{' '}
          at{' '}
          <Text as="span" fontStyle="italic">
            {companyName}
          </Text>
        </Text>
        <Text fontSize="16px" color="text.main">
          Automatically promote your job, reach out to top candidates, and deliver qualified applicants.
        </Text>
      </Box>
    </Flex>

    <Box px="16px">
      <Flex justifyContent="space-between" alignItems="flex-start" mb="4px">
        <Box flex="1">
          <Text fontSize="16px" fontWeight="500" color="text.dark" mb="8px">
            Application Boost Agent
          </Text>

          <Flex flexDirection="column" sx={{ gap: '4px' }}>
            <Flex alignItems="center" sx={{ gap: '8px' }}>
              <Box as={Check} width="16px" height="16px" sx={{ color: 'purple.500', flexShrink: 0 }} />
              <Text fontSize="14px" color="text.main">
                Get 30–60 new highly qualified candidates
              </Text>
            </Flex>
            <Flex alignItems="center" sx={{ gap: '8px' }}>
              <Box as={Check} width="16px" height="16px" sx={{ color: 'purple.500', flexShrink: 0 }} />
              <Text fontSize="14px" color="text.main">
                Automatic sourcing and reach out
              </Text>
            </Flex>
            <Flex alignItems="center" sx={{ gap: '8px' }}>
              <Box as={Check} width="16px" height="16px" sx={{ color: 'purple.500', flexShrink: 0 }} />
              <Text fontSize="14px" color="text.main">
                Job promotion across multiple methods
              </Text>
            </Flex>
          </Flex>
        </Box>
        <Text fontSize="14px" fontWeight="medium" color="text.dark" whiteSpace="nowrap">
          {APPLICATION_BOOST_CREDITS} credits
        </Text>
      </Flex>
    </Box>

    <Box
      onClick={handleCalibrationToggle}
      sx={{
        cursor: 'pointer',
        bg: isCalibrationAgentSelected ? 'white' : 'transparent',
        border: '1px solid',
        borderColor: isCalibrationAgentSelected ? 'transparent' : 'border.subtle',
        borderRadius: '4px',
        p: '16px',
        transition: 'all 0.2s ease-in-out',
        ...(isCalibrationAgentSelected && {
          backgroundImage:
            'linear-gradient(white, white), linear-gradient(94deg, #4900FF 3.21%, #D400FF 63.82%, #FF9257 134.26%)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
        }),
      }}
    >
      <Flex justifyContent="space-between" alignItems="flex-start">
        <Flex alignItems="flex-start" sx={{ gap: '16px' }} flex="1">
          <Box onClick={(event) => event.stopPropagation()}>
            <Checkbox
              containerProps={{ width: '16px', minWidth: '16px', mt: '4px' }}
              checked={isCalibrationAgentSelected}
              onChange={(event) => {
                event.stopPropagation();
                handleCalibrationToggle();
              }}
            />
          </Box>
          <Box flex="1">
            <Flex alignItems="center" sx={{ gap: '8px' }} mb="4px">
              <Text fontSize="16px" fontWeight="medium" color="text.dark">
                Calibration Agent
              </Text>
              <Flex alignItems="center" sx={{ gap: '4px' }} bg="success.light" borderRadius="4px">
                <Box as={TrendingUp} width="16px" height="16px" sx={{ color: 'icon.success' }} />
                <Text fontSize="14px" fontWeight="medium" color="icon.success">
                  +20% best results
                </Text>
              </Flex>
            </Flex>
            <Text fontSize="14px" color="text.main">
              Improve candidate quality by refining candidate requirements before you start your application boost.
            </Text>
          </Box>
        </Flex>
        <Text fontSize="14px" fontWeight="medium" color="text.dark" ml="8px" whiteSpace="nowrap">
          {CALIBRATION_AGENT_CREDITS} credits
        </Text>
      </Flex>
    </Box>

    <Flex sx={{ gap: '16px', flexDirection: 'column' }}>
      <Flex justifyContent="space-between" alignItems="flex-start" px="16px">
        <Flex sx={{ gap: '4px', flexDirection: 'column' }}>
          <Text fontSize="16px" fontWeight="500" color="text.dark">
            Total
          </Text>
          <Text fontSize="14px" color="text.subtle">
            {availableAgentCredits > 0
              ? `${Intl.NumberFormat('en-US').format(availableAgentCredits)} available credits`
              : 'No credits available'}
          </Text>
        </Flex>
        <Text fontSize="16px" fontWeight="500" color="text.dark">
          {Intl.NumberFormat('en-US').format(totalCredits)} credits
        </Text>
      </Flex>
      {availableAgentCredits < totalCredits && (
        <Box
          sx={{
            bg: 'white',
            border: '1px solid',
            borderColor: 'transparent',
            borderRadius: '4px',
            p: '16px',
            backgroundImage:
              'linear-gradient(rgba(252, 247, 255, 1), rgba(252, 247, 255, 1)), linear-gradient(94deg, #4900FF 3.21%, #D400FF 63.82%, #FF9257 134.26%)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
          }}
        >
          <Flex justifyContent="space-between" alignItems="center">
            <Flex alignItems="flex-start" sx={{ gap: '16px' }} flex="1">
              <Image src={IconStart} alt="Calibration Agent" width="24px" height="24px" />
              <Box flex="1">
                <Text fontSize="14px" lineHeight="1.4" fontWeight="500" color="text.dark">
                  Get more credits to keep boosting jobs{' '}
                </Text>
                <Text fontSize="14px" color="text.main">
                  You don’t have enough credits for this boost. Contact us to top up your balance.{' '}
                </Text>
              </Box>
            </Flex>
            <Box>
              <Button
                variant="primary"
                width="100%"
                justifyContent="center"
                onClick={() => {
                  window.open(CONTACT_AGENT_BOOST_MEETING_URL, '_blank', 'noopener,noreferrer');
                }}
                icon={ArrowRight}
                iconPosition="right"
                iconGap="8px"
                size="small"
              >
                Let&apos;s talk
              </Button>
            </Box>
          </Flex>
        </Box>
      )}
    </Flex>

    <Button
      variant="primary"
      width="100%"
      justifyContent="center"
      onClick={handleStartAgent}
      loading={isLoading}
      disabled={isLoading || availableAgentCredits < totalCredits}
      icon={Rocket}
      iconPosition="right"
      iconGap="8px"
    >
      Boost job
    </Button>
  </Box>
);

AgentBoost.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    organization: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  handleStartAgent: PropTypes.func.isRequired,
  handleCalibrationToggle: PropTypes.func.isRequired,
  companyName: PropTypes.string.isRequired,
  jobTitle: PropTypes.string.isRequired,
  isCalibrationAgentSelected: PropTypes.bool.isRequired,
  totalCredits: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  availableAgentCredits: PropTypes.number.isRequired,
};
